import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

const customStyles = {
  atmosphere: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: -1,
    background: `
      radial-gradient(circle at 80% 20%, rgba(168, 192, 216, 0.6) 0%, transparent 40%),
      radial-gradient(circle at 10% 60%, rgba(196, 214, 233, 0.5) 0%, transparent 50%),
      radial-gradient(circle at 90% 90%, rgba(168, 192, 216, 0.4) 0%, transparent 40%)
    `,
    filter: 'blur(20px)',
    pointerEvents: 'none',
  },
  diagramContainer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    pointerEvents: 'none',
    overflow: 'hidden',
    zIndex: 0,
  },
  shapeStarburst: {
    position: 'absolute',
    top: '18%',
    right: '-10%',
    width: '200px',
    height: '200px',
  },
  shapeSpiral: {
    position: 'absolute',
    top: '45%',
    left: '-20px',
    width: '250px',
    height: '150px',
    transform: 'rotate(-10deg)',
    opacity: 0.8,
  },
  shapeOrbits: {
    position: 'absolute',
    top: '65%',
    right: '10px',
    width: '180px',
    height: '220px',
  },
  shapeDot: {
    position: 'absolute',
    borderRadius: '50%',
    backgroundColor: '#1a1a1a',
  },
  dot1: { top: '12%', left: '15%', width: '12px', height: '12px' },
  dot2: { top: '15%', left: '8%', width: '24px', height: '24px' },
  dot3: { top: '85%', right: '25%', width: '18px', height: '18px' },
  shapeEye: {
    position: 'absolute',
    top: '32%',
    left: '15%',
    width: '140px',
    height: '80px',
    border: '1.5px solid #1a1a1a',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: 'rotate(-15deg)',
  },
  eyePupil: {
    width: '20px',
    height: '20px',
    background: '#1a1a1a',
    borderRadius: '50%',
  },
  labelFloat: {
    position: 'absolute',
    fontSize: '1rem',
    fontWeight: 600,
  },
  starburstRing: {
    position: 'absolute',
    top: '15%',
    right: '-15%',
    width: '220px',
    height: '220px',
    borderRadius: '50%',
    background: 'repeating-conic-gradient(#1a1a1a 0% 0.5%, transparent 0.5% 2%)',
    maskImage: 'radial-gradient(circle, transparent 20%, black 25%)',
    WebkitMaskImage: 'radial-gradient(circle, transparent 20%, black 25%)',
  },
  starburstCenter: {
    position: 'absolute',
    top: '15%',
    right: '-15%',
    width: '220px',
    height: '220px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  starburstDot: {
    width: '40px',
    height: '40px',
    background: '#1a1a1a',
    borderRadius: '50%',
  },
};

const MenuPage = () => {
  const starburstRef = useRef(null);
  const spiralRef = useRef(null);
  const orbitsRef = useRef(null);
  const eyeRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (starburstRef.current) {
        starburstRef.current.style.transform = `translateY(${scrollY * 0.1}px) rotate(${scrollY * 0.05}deg)`;
      }
      if (spiralRef.current) {
        spiralRef.current.style.transform = `translateY(${scrollY * -0.05}px) rotate(-10deg)`;
      }
      if (orbitsRef.current) {
        orbitsRef.current.style.transform = `translateY(${scrollY * 0.2}px)`;
      }
      if (eyeRef.current) {
        eyeRef.current.style.transform = `translateY(${scrollY * 0.08}px) rotate(-15deg)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Helvetica+Neue&display=swap');
      * { box-sizing: border-box; margin: 0; padding: 0; -webkit-font-smoothing: antialiased; }
      body { background-color: #e6e4de; overflow-x: hidden; }
      @keyframes rotateSlow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      .starburst-anim { animation: rotateSlow 60s linear infinite; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const menuSections = [
    {
      header: '(WOOD-FIRED)',
      items: [
        { index: 'A.', name: 'Margherita', price: '18', notes: 'San Marzano tomato, fior di latte, basil, extra virgin olive oil.' },
        { index: 'B.', name: 'Burrata & Prosciutto', price: '24', notes: 'Creamy burrata, aged prosciutto, arugula, balsamic reduction.' },
        { index: 'C.', name: 'Funghi', price: '22', notes: 'Wild mushrooms, truffle oil, thyme, fontina, garlic cream.' },
        { index: 'D.', name: 'Diavola', price: '21', notes: 'Spicy soppressata, calabrian chili, honey, mozzarella.' },
        { index: 'E.', name: 'Mortadella & Pistachio', price: '23', notes: 'Mortadella, crushed pistachios, stracciatella, lemon zest.' },
        { index: 'F.', name: 'White Clam', price: '26', notes: 'Manila clams, white wine, garlic, chili flake, parsley.' },
      ],
    },
    {
      header: '(APERITIVO)',
      items: [
        { index: 'G.', name: 'Classic Negroni', price: '16', notes: 'Tanqueray gin, Campari, Carpano Antica vermouth.' },
        { index: 'H.', name: 'Negroni Sbagliato', price: '17', notes: 'Prosecco, Campari, sweet vermouth, orange twist.' },
        { index: 'I.', name: 'White Negroni', price: '18', notes: 'Gin, Suze gentian, Lillet Blanc, grapefruit.' },
      ],
    },
  ];

  return (
    <div
      style={{
        backgroundColor: '#e6e4de',
        color: '#1a1a1a',
        fontFamily: '"Helvetica Neue", "Helvetica", "Arial", sans-serif',
        overflowX: 'hidden',
        width: '100%',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      {/* Atmosphere */}
      <div style={customStyles.atmosphere}></div>

      {/* Diagram Container */}
      <div style={customStyles.diagramContainer}>
        {/* Starburst SVG */}
        <svg
          ref={starburstRef}
          className="starburst-anim"
          style={customStyles.shapeStarburst}
          viewBox="0 0 100 100"
        >
          <g stroke="#1a1a1a" strokeWidth="0.3">
            <circle cx="50" cy="50" r="15" fill="#1a1a1a" />
            <path d="M50 50 L50 0" />
            <path d="M50 50 L50 100" />
            <path d="M50 50 L0 50" />
            <path d="M50 50 L100 50" />
          </g>
          <circle cx="50" cy="50" r="48" fill="none" stroke="#1a1a1a" strokeWidth="0.5" strokeDasharray="1 2" />
          <circle cx="50" cy="50" r="40" fill="none" stroke="#1a1a1a" strokeWidth="0.5" strokeDasharray="1 1.5" />
          <circle cx="50" cy="50" r="32" fill="none" stroke="#1a1a1a" strokeWidth="0.5" strokeDasharray="1 1" />
          <circle cx="50" cy="50" r="24" fill="none" stroke="#1a1a1a" strokeWidth="0.5" strokeDasharray="0.5 0.5" />
        </svg>

        {/* Conic Gradient Ring */}
        <div style={customStyles.starburstRing}></div>
        <div style={customStyles.starburstCenter}>
          <div style={customStyles.starburstDot}></div>
        </div>
        <div style={{ ...customStyles.labelFloat, top: '13%', right: '45%' }}>B.</div>

        {/* Spiral SVG */}
        <svg
          ref={spiralRef}
          style={customStyles.shapeSpiral}
          viewBox="0 0 200 100"
          fill="none"
          stroke="#1a1a1a"
          strokeWidth="1.2"
        >
          <path d="M10,50 Q30,0 50,50 T90,50 T130,50 T170,50" strokeOpacity="0.2" />
          <path d="M10,50 C10,20 40,20 40,50 C40,80 70,80 70,50 C70,20 100,20 100,50 C100,80 130,80 130,50 C130,20 160,20 160,50" />
        </svg>
        <div style={{ ...customStyles.labelFloat, top: '43%', left: '10%' }}>A.</div>

        {/* Orbits SVG */}
        <svg
          ref={orbitsRef}
          style={customStyles.shapeOrbits}
          viewBox="0 0 100 100"
          fill="none"
          stroke="#1a1a1a"
          strokeWidth="1"
        >
          <ellipse cx="50" cy="50" rx="20" ry="45" transform="rotate(30 50 50)" />
          <ellipse cx="50" cy="50" rx="20" ry="45" transform="rotate(-30 50 50)" />
          <circle cx="70" cy="20" r="4" fill="#1a1a1a" stroke="none" />
        </svg>
        <div style={{ ...customStyles.labelFloat, top: '63%', right: '55%' }}>C.</div>

        {/* Dots */}
        <div style={{ ...customStyles.shapeDot, ...customStyles.dot1 }}></div>
        <div style={{ ...customStyles.shapeDot, ...customStyles.dot2, position: 'absolute' }}>
          <div style={{ position: 'absolute', top: '-15px', left: '0', fontSize: '0.8rem', fontWeight: 600 }}>D.</div>
        </div>
        <div style={{ ...customStyles.shapeDot, ...customStyles.dot3 }}></div>

        {/* Eye Shape */}
        <div ref={eyeRef} style={customStyles.shapeEye}>
          <div style={customStyles.eyePupil}></div>
        </div>
      </div>

      {/* Main Content */}
      <main style={{ position: 'relative', zIndex: 2 }}>
        <h1
          style={{
            fontSize: '2.2rem',
            fontWeight: 500,
            letterSpacing: '-0.02em',
            textAlign: 'center',
            borderBottom: '2px solid #1a1a1a',
            paddingBottom: '1rem',
            margin: '3rem 1.5rem 2rem 1.5rem',
            lineHeight: 1,
          }}
        >
          The Canopy
        </h1>

        {menuSections.map((section, sIdx) => (
          <div key={sIdx}>
            <div
              style={{
                textAlign: 'center',
                textTransform: 'uppercase',
                fontSize: '1.1rem',
                letterSpacing: '0.05em',
                margin: '4rem 0 2rem 0',
              }}
            >
              <span style={{ display: 'inline-block', transform: 'scaleY(1.1)' }}>{section.header}</span>
            </div>
            <ul
              style={{
                listStyle: 'none',
                padding: '0 2rem',
                marginBottom: '4rem',
                position: 'relative',
                zIndex: 2,
              }}
            >
              {section.items.map((item, iIdx) => (
                <React.Fragment key={iIdx}>
                  <li
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      marginBottom: '0.8rem',
                      fontSize: '1.1rem',
                      fontWeight: 400,
                      lineHeight: 1.4,
                    }}
                  >
                    <span style={{ fontWeight: 600, marginRight: '0.5rem', minWidth: '1.5rem' }}>{item.index}</span>
                    <span style={{ flexGrow: 1 }}>{item.name}</span>
                    <span style={{ marginLeft: '1rem', fontVariantNumeric: 'tabular-nums' }}>{item.price}</span>
                  </li>
                  <div
                    style={{
                      fontSize: '0.85rem',
                      color: '#444',
                      paddingLeft: '2rem',
                      marginTop: '-0.4rem',
                      marginBottom: '1.2rem',
                      opacity: 0.8,
                      maxWidth: '90%',
                    }}
                  >
                    {item.notes}
                  </div>
                </React.Fragment>
              ))}
            </ul>
          </div>
        ))}

        <div
          style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            fontSize: '0.9rem',
            borderTop: '1px solid rgba(0,0,0,0.1)',
            marginTop: '4rem',
          }}
        >
          <p>FIG. 1.1 — Current Menu</p>
          <p style={{ marginTop: '10px', opacity: 0.6 }}>(WOOD-FIRED & HANDCRAFTED)</p>
        </div>
      </main>
    </div>
  );
};

const App = () => {
  return (
    <Router basename="/">
      <Routes>
        <Route path="/" element={<MenuPage />} />
      </Routes>
    </Router>
  );
};

export default App;
